/**
 * Users Management Page
 *
 * Mobile-first user list with cards on mobile, table on desktop.
 * Now navigates to dedicated pages instead of using dialogs.
 */

import { useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Users, Pencil, Trash2, Shield, ShieldCheck, ShieldAlert, ChevronRight, Eye } from 'lucide-react'

import api from '@/lib/api'
import { debounce } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { UserAvatar } from '@/components/AvatarSelector'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface User {
  id: number
  username: string
  full_name: string
  email: string | null
  role: string
  is_active: boolean
  last_login: string | null
  avatar: string | null
}

export function UsersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ['users', searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (searchQuery) params.set('search', searchQuery)
      const response = await api.get(`/users/?${params}`)
      return response.data
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/users/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    },
  })

  const handleDeleteClick = (user: User, e: React.MouseEvent) => {
    e.stopPropagation()
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id)
    }
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <ShieldAlert className="h-4 w-4 text-red-500" />
      case 'manager':
        return <ShieldCheck className="h-4 w-4 text-blue-500" />
      default:
        return <Shield className="h-4 w-4 text-gray-500" />
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return t('users.admin')
      case 'manager':
        return t('users.manager')
      default:
        return t('users.seller')
    }
  }

  return (
    <div className="space-y-4">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">{t('users.title')}</h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {data?.items?.length || 0} {t('users.title').toLowerCase()}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButtons
            data={data?.items || []}
            filename="usuarios"
            columns={[
              { key: 'username', header: t('users.username') },
              { key: 'full_name', header: t('users.fullName') },
              { key: 'email', header: t('clients.email') },
              { key: 'role', header: t('users.role'), format: (v) => getRoleLabel(v as string) },
            ]}
          />
          <Button onClick={() => navigate('/users/new')} className="hidden md:flex">
            <Plus className="h-4 w-4 mr-2" />
            {t('users.newUser')}
          </Button>
        </div>
      </div>

      {/* Search - Touch friendly */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder={t('common.search')}
          className="pl-10 h-12 text-base"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{t('common.noResults')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery ? t('common.noResults') : t('users.addFirst')}
          </p>
          {!searchQuery && (
            <Button onClick={() => navigate('/users/new')}>
              <Plus className="h-4 w-4 mr-2" />
              {t('users.newUser')}
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Cards */}
          <div className="space-y-3 lg:hidden">
            {data?.items?.map((user: User) => (
              <Link
                key={user.id}
                to={`/users/${user.id}`}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
              <Card
                className="active:bg-muted/50 cursor-pointer touch-manipulation"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      avatarId={user.avatar}
                      name={user.full_name}
                      size="lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getRoleIcon(user.role)}
                        <p className="font-medium truncate">{user.full_name}</p>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        @{user.username}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? t('common.active') : t('common.inactive')}
                      </Badge>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden lg:block">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium w-16"></th>
                    <th className="text-left p-4 font-medium">{t('users.fullName')}</th>
                    <th className="text-left p-4 font-medium">{t('clients.email')}</th>
                    <th className="text-left p-4 font-medium">{t('users.role')}</th>
                    <th className="text-left p-4 font-medium">{t('common.status')}</th>
                    <th className="text-right p-4 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((user: User) => (
                    <tr key={user.id} className="border-b hover:bg-muted/50">
                      <td className="p-4">
                        <UserAvatar
                          avatarId={user.avatar}
                          name={user.full_name}
                          size="sm"
                        />
                      </td>
                      <td className="p-4 font-medium">{user.full_name}</td>
                      <td className="p-4 break-all">{user.email || '-'}</td>
                      <td className="p-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getRoleIcon(user.role)}
                          {getRoleLabel(user.role)}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant={user.is_active ? 'success' : 'secondary'}>
                          {user.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/users/${user.id}`)}
                            title={t('common.view')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/users/${user.id}/edit`)}
                            title={t('common.edit')}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteClick(user, e)}
                            title={t('common.delete')}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Mobile FAB for New User */}
      <Button
        onClick={() => navigate('/users/new')}
        className="md:hidden fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg z-10"
        size="icon"
      >
        <Plus className="h-6 w-6" />
      </Button>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('users.deleteUser')}</DialogTitle>
            <DialogDescription>
              {t('users.confirmDelete')} "{userToDelete?.full_name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="w-full sm:w-auto"
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
